import React, { useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { chaseStaff, errorMessage, isOk } from '../../helpers/trainingApi'
import { cellState, needsChasing } from './matrixStatus'

// Sends a reminder about one course to the staff who need it and do not
// hold it in date. The list is shown first so nobody is chased by surprise.
const ChaseDialog = ({ open, onClose, course, employees = [] }) => {
  const candidates = useMemo(
    () => (course ? employees.filter((employee) => needsChasing(employee.cells?.[course._id])) : []),
    [course, employees],
  )
  const [selected, setSelected] = useState([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [sent, setSent] = useState(null)

  useEffect(() => {
    if (!open) return
    setSelected(candidates.map((employee) => employee._id))
    setError(null)
    setSent(null)
  }, [open, candidates])

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]))
  const allSelected = candidates.length > 0 && selected.length === candidates.length

  const handleSend = async () => {
    setSending(true)
    setError(null)
    const res = await chaseStaff(course._id, selected)
    setSending(false)
    if (isOk(res)) setSent(res.data?.sent ?? 0)
    else setError(errorMessage(res, 'Unable to send the reminders. Please try again.'))
  }

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Remind staff: {course?.title}</DialogTitle>
      <DialogContent>
        {sent !== null ? (
          <Alert severity="success">
            Reminder sent to {sent} member{sent === 1 ? '' : 's'} of staff.
          </Alert>
        ) : candidates.length === 0 ? (
          <Typography color="text.secondary">
            Nobody who needs this course is overdue or still to complete it.
          </Typography>
        ) : (
          <Stack gap={1}>
            <Typography variant="body2" color="text.secondary">
              These staff need this course and are overdue or have not completed it. Each person
              ticked gets a notification asking them to complete it.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={selected.length > 0 && !allSelected}
                  onChange={() => setSelected(allSelected ? [] : candidates.map((employee) => employee._id))}
                />
              }
              label={<b>Everyone listed ({candidates.length})</b>}
            />
            <Stack sx={{ maxHeight: 320, overflow: 'auto', pl: 1 }}>
              {candidates.map((employee) => (
                <FormControlLabel
                  key={employee._id}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.includes(employee._id)}
                      onChange={() => toggle(employee._id)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {employee.firstName} {employee.lastName}
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' · '}
                        {cellState(employee.cells[course._id]).label}
                        {employee.clinicalRole ? ` · ${employee.clinicalRole}` : ''}
                      </Typography>
                    </Typography>
                  }
                />
              ))}
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={sending}>
          {sent !== null ? 'Close' : 'Cancel'}
        </Button>
        {sent === null && candidates.length > 0 && (
          <Button variant="contained" color="third" onClick={handleSend} disabled={sending || selected.length === 0}>
            {sending ? 'Sending...' : `Send ${selected.length} reminder${selected.length === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ChaseDialog
